import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useApi } from "../api/client";
import { Issue, IssueStatus } from "../types/projects";

const COLUMNS: { status: IssueStatus; label: string }[] = [
  { status: "todo", label: "TODO" },
  { status: "wip", label: "WIP" },
  { status: "done", label: "DONE" },
];

function issueLogText(issue: Issue): string {
  const title = issue.title.trim();
  const notes = issue.notes.trim();
  return notes ? `${title}\n${notes}` : title;
}

function IssueCard({
  issue,
  onDelete,
  onCopyToLog,
}: {
  issue: Issue;
  onDelete: (issue: Issue) => void;
  onCopyToLog: (text: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: issue.id,
      data: { status: issue.status },
    });

  return (
    <Paper
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{
        p: 1.5,
        mb: 1,
        cursor: "grab",
        opacity: isDragging ? 0.4 : 1,
        transform: CSS.Translate.toString(transform),
        "&:active": { cursor: "grabbing" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {issue.title}
          </Typography>
          {issue.notes && (
            <Typography variant="body2" color="text.secondary">
              {issue.notes}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <IconButton
            aria-label="Copy to worked day notes"
            size="small"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => onCopyToLog(issueLogText(issue))}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="Delete issue"
            size="small"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => onDelete(issue)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
}

function IssueColumn({
  status,
  label,
  issues,
  onDelete,
  onCopyToLog,
}: {
  status: IssueStatus;
  label: string;
  issues: Issue[];
  onDelete: (issue: Issue) => void;
  onCopyToLog: (text: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${status}`,
    data: { status },
  });

  return (
    <Paper
      ref={setNodeRef}
      sx={{
        p: 2,
        minHeight: 280,
        backgroundColor: isOver ? "#e8eefc" : "#fafafa",
        border: isOver ? "1px dashed #102781" : "1px solid transparent",
      }}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>
        {label}
        <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
          {issues.length}
        </Typography>
      </Typography>
      {issues.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Drop issues here
        </Typography>
      ) : (
        issues.map((issue) => (
          <IssueCard
            key={issue.id}
            issue={issue}
            onDelete={onDelete}
            onCopyToLog={onCopyToLog}
          />
        ))
      )}
    </Paper>
  );
}

const IssueBoard: React.FC<{
  projectId: string;
  issues: Issue[];
  onCopyToLog: (text: string) => void;
}> = ({ projectId, issues: initialIssues, onCopyToLog }) => {
  const { request } = useApi();
  const [issues, setIssues] = useState<Issue[]>(initialIssues);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeIssue, setActiveIssue] = useState<Issue | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIssues(initialIssues);
  }, [initialIssues]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const byStatus = useMemo(() => {
    const grouped: Record<IssueStatus, Issue[]> = {
      todo: [],
      wip: [],
      done: [],
    };
    for (const issue of issues) {
      grouped[issue.status].push(issue);
    }
    for (const status of Object.keys(grouped) as IssueStatus[]) {
      grouped[status].sort((a, b) => a.order - b.order);
    }
    return grouped;
  }, [issues]);

  const createIssue = async () => {
    if (!title.trim()) {
      return;
    }
    setSaving(true);
    try {
      setError(null);
      const created = await request<Issue>("/api/issues", {
        method: "POST",
        body: JSON.stringify({
          projectId,
          title: title.trim(),
          notes: notes.trim(),
        }),
      });
      setIssues((current) => [...current, created]);
      setTitle("");
      setNotes("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add issue");
    } finally {
      setSaving(false);
    }
  };

  const deleteIssue = async (issue: Issue) => {
    if (!window.confirm(`Delete "${issue.title}"?`)) {
      return;
    }
    try {
      setError(null);
      await request(`/api/issues/${issue.id}`, { method: "DELETE" });
      setIssues((current) => current.filter((item) => item.id !== issue.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete issue");
    }
  };

  const moveIssue = async (issueId: string, status: IssueStatus) => {
    const issue = issues.find((item) => item.id === issueId);
    if (!issue || issue.status === status) {
      return;
    }
    const order =
      Math.max(0, ...byStatus[status].map((item) => item.order)) + 1;
    const previous = issues;
    setIssues((current) =>
      current.map((item) =>
        item.id === issueId ? { ...item, status, order } : item,
      ),
    );
    try {
      setError(null);
      await request(`/api/issues/${issueId}`, {
        method: "PATCH",
        body: JSON.stringify({ status, order }),
      });
    } catch (err) {
      setIssues(previous);
      setError(err instanceof Error ? err.message : "Failed to move issue");
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveIssue(null);
    const { active, over } = event;
    if (!over) {
      return;
    }

    const overId = String(over.id);
    let nextStatus: IssueStatus | undefined;
    if (overId.startsWith("column-")) {
      nextStatus = overId.replace("column-", "") as IssueStatus;
    } else {
      nextStatus = issues.find((item) => item.id === overId)?.status;
    }

    if (nextStatus) {
      moveIssue(String(active.id), nextStatus);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Issues
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr auto" },
          gap: 2,
          mb: 3,
          alignItems: "start",
        }}
      >
        <TextField
          label="Issue title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              createIssue();
            }
          }}
          fullWidth
        />
        <TextField
          label="Notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          fullWidth
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={createIssue}
          disabled={saving || !title.trim()}
          sx={{ height: 56 }}
        >
          Add
        </Button>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={(event) => {
          const issue = issues.find((item) => item.id === String(event.active.id));
          setActiveIssue(issue ?? null);
        }}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveIssue(null)}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
            gap: 2,
          }}
        >
          {COLUMNS.map((column) => (
            <IssueColumn
              key={column.status}
              status={column.status}
              label={column.label}
              issues={byStatus[column.status]}
              onDelete={deleteIssue}
              onCopyToLog={onCopyToLog}
            />
          ))}
        </Box>
        <DragOverlay>
          {activeIssue ? (
            <Paper sx={{ p: 1.5, width: 260 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {activeIssue.title}
              </Typography>
            </Paper>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Paper>
  );
};

export default IssueBoard;
